import { useState } from 'react';
import { makeStyles } from '@mui/styles';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Grid, TextField } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { submittedDot } from '#app/fullstory-events/events';
import { SubmitHandler, useForm } from 'react-hook-form';
import { snakeToCamelCase } from '#app/utils';

const useStyles = makeStyles((theme: Theme) => ({
  wrapper: {
    display: 'flex',
    justifyContent: 'flex-start',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 40,
  },
  label: {
    [theme.breakpoints.down('md')]: {
      fontSize: 24,
    },
    [theme.breakpoints.up('md')]: {
      fontSize: 32,
    },
    fontWeight: 700,
    marginBottom: theme.spacing(2),
    textAlign: 'left',
  },
  buttonText: {
    textTransform: 'capitalize',
    fontWeight: 700,
  },
}));

type FormValues = {
  dotNumber: number;
};

type DotFormError = {
  msg: string;
  field?: any;
};

function DotForm() {
  const classes = useStyles();
  const [disableButtons, setDisableButtons] = useState(false);
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<FormValues>();
  const navigate = useNavigate();

  const source = 'web-app';
  const [searchParams] = useSearchParams();

  const onSubmit: SubmitHandler<FormValues> = async (data, e) => {
    setDisableButtons(true);

    try {
      const response = await fetch('/api/registration/applicant-initializer', {
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'POST',
        body: JSON.stringify({
          dot_number: Number(data.dotNumber),
          source: source,
          utm_source: searchParams.get("utm_source"),
          utm_campaign: searchParams.get("utm_campaign"),
          utm_content: searchParams.get("utm_content"),
          utm_medium: searchParams.get("utm_medium"),
          fbclid: searchParams.get("fbclid"),
          gclid: searchParams.get("gclid")
        }),
      });

      const body = await response.json();

      if (!response.ok) {
        if (body.errors) {
          body.errors.forEach((error: DotFormError) => {
            if (error.field) {
              setError(snakeToCamelCase(error.field) as keyof FormValues, {
                type: 'custom',
                message: error.msg,
              });
            } else {
              throw new Error(
                `An error has occurred: ${response.status}; ${JSON.stringify(
                  body.errors
                )}`
              );
            }
          });
        } else {
          throw new Error(`An error has occurred: ${response.status}`);
        }
      } else {
        navigate('/registration/account-details', {
          state: { data: body },
        });
      }
    } catch (e) {
      setError('dotNumber', {
        type: 'custom',
        message:
          'There was an issue processing your request. Please try again later or click Get Help.',
      });
      console.error('Failed to check DOT number', e);
    } finally {
      submittedDot({ dotNumber: data.dotNumber });
      setDisableButtons(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Grid className={classes.wrapper} container>
        <div className={classes.label}>Create your account</div>
        <TextField
          data-testid="input-dotNumber"
          error={!!errors.dotNumber?.type}
          fullWidth
          helperText={errors.dotNumber?.message}
          id="dotNumber"
          placeholder="DOT #"
          label="DOT #"
          variant="outlined"
          {...register('dotNumber', {
            required: 'Please enter your DOT number',
            maxLength: {
              value: 10,
              message: 'Please enter a maximum of 10 digits',
            },
            pattern: {
              value: /^\d+$/,
              message: 'Please enter a valid DOT number--numbers only',
            },
          })}
        />
        <Button
          color="primary"
          data-testid="submit-dotNumber"
          disabled={disableButtons}
          fullWidth
          style={{
            height: 56,
            marginTop: 32,
          }}
          type="submit"
          variant="contained">
          <span className={classes.buttonText}>Continue</span>
        </Button>
      </Grid>
    </form>
  );
}

export default DotForm;
